import {
  displayName,
  stripPriorityPrefix,
} from "shared/components/vega/LumenV2/utils";
import {
  SEVERITY_ORDER,
  Vero1OutcomeOptions,
  Vero1RowKind,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import { formatDate } from "shared/utils/ui";

export { displayName };

/**
 * Criterion values arrive as whatever Lumen annotated the run with: a number
 * for scores and ratios, a boolean for checks, and an array of record objects
 * for the records criteria (evictions, criminal, collections, public credit).
 *
 * Rendering those objects directly gives "[object Object]", so records are
 * summarised to a count with a readable line each. Nothing here is criterion-
 * specific — the shape drives the output, so a new records criterion needs no
 * change.
 */

/**
 * Categories arrive as the backend's raw slugs (income / credit / identity /
 * rental / criminal). Titles follow the portal's existing vocabulary so the
 * panel reads the same as the rest of the deal.
 */
// the sequence — not the values — follows CategoryId in
// app-summary/AppListV2/constants.ts so the checklist reads in the same order
// as the summary cards. These are the checklist's own category slugs and do
// NOT match CategoryId's ("credit" here vs "credit-history" there); they come
// from the backend row's `category`. Do not unify the two lists.
export const CATEGORY_ORDER = [
  "identity",
  "income",
  "credit",
  "rental",
  "criminal",
];

export const categorySortKey = (category: string): number => {
  const index = CATEGORY_ORDER.indexOf(category);
  return index === -1 ? CATEGORY_ORDER.length : index;
};

const CATEGORY_LABELS: Record<string, string> = {
  income: "Income & Assets",
  credit: "Credit",
  identity: "Identity",
  rental: "Rental History",
  criminal: "Criminal History",
};

export const formatCategory = (category: string) =>
  CATEGORY_LABELS[category] || category || "Other";

/**
 * Money criteria are annotated in cents, like the rest of the platform —
 * verified on deal 50325, where income 500000 against rent 200000 yields the
 * stored 0.208 ratio only if both are cents. Rendering the raw number would
 * read $500,000 for a $5,000 income.
 */
export const MONEY_CRITERIA = new Set([
  "total-verified-income",
  "total-verified-assets",
  "max-collections-amount",
]);

/** Ratios read as multiples of rent, not bare decimals. */
const RATIO_CRITERIA = new Set(["income-to-rent", "cash-runway"]);

/** A missing credit score arrives as 0 or null. */
const SCORE_CRITERIA = new Set(["avg-credit-score"]);

export const NO_SCORE_LABEL = "No score";

export const isScoreCriterion = (criterionKey: string): boolean =>
  SCORE_CRITERIA.has(criterionKey);

const isMissingScore = (value: unknown): boolean =>
  typeof value === "number"
    ? value === 0
    : typeof value === "string"
    ? value.trim() === "" || Number(value) === 0
    : value === null || value === undefined;

const ANNOTATION_ONLY_CRITERIA = new Set(["identity-fraud-risk-needs-review"]);

export const isAnnotationOnlyCriterion = (criterionKey: string): boolean =>
  ANNOTATION_ONLY_CRITERIA.has(criterionKey);

/** Wire-driven where the payload carries it; key-set fallback otherwise. */
export const isAnnotationOnlyItem = (item: {
  isAnnotationOnly?: boolean | null;
  criterionKey: string;
}): boolean =>
  item.isAnnotationOnly ?? isAnnotationOnlyCriterion(item.criterionKey);

const CHECK_STATE_LABELS: Record<string, string> = {
  PASS: "Pass",
  NEEDS_REVIEW: "Needs Review",
  FAIL: "Fail",
  ERROR: "Error",
};

export const checkStateLabel = (value: unknown): string | null =>
  typeof value === "string" && value in CHECK_STATE_LABELS
    ? CHECK_STATE_LABELS[value]
    : null;

export type RowTone = Vero1Severity;

export const SEVERITY_WORDS: Record<Vero1Severity, string> = {
  pass: "Pass",
  warn: "Warn",
  fail: "Fail",
};

const PLAIN_SEVERITY_NAMES: Record<Vero1Severity, string> = {
  pass: "Approve",
  warn: "Conditional",
  fail: "Decline",
};

export const NO_VERDICT_LABEL = "No verdict";

export const severityLabel = (severity?: Vero1Severity | null): string =>
  severity ? SEVERITY_WORDS[severity] : NO_VERDICT_LABEL;

interface SeveritySource {
  originalSeverity?: Vero1Severity | null;
  overrideSeverity?: Vero1Severity | null;
}

interface NamingSource extends SeveritySource {
  rowKind?: Vero1RowKind | string;
  outcomeOptions?: Vero1OutcomeOptions | null;
  availableSeverities?: Vero1Severity[] | null;
  nameCandidates?: Partial<Record<Vero1Severity, string[]>> | null;
  originalValue?: unknown;
}

export const lumenTone = (item: SeveritySource): RowTone | null =>
  item.originalSeverity ?? null;

export const effectiveTone = (item: SeveritySource): RowTone | null =>
  item.overrideSeverity ?? item.originalSeverity ?? null;

const outcomesOf = (item: NamingSource) => item.outcomeOptions?.outcomes ?? [];

/** Mirrors Vero1ChecklistItem.client_name_for. */
export const clientNameFor = (
  severity: Vero1Severity | null | undefined,
  item: NamingSource
): string | null => {
  if (!severity || item.rowKind === Vero1RowKind.Sentilink) return null;
  const outcomes = outcomesOf(item);
  const fired = new Set(item.outcomeOptions?.fired ?? []);
  const ordered =
    severity === item.originalSeverity
      ? [
          ...outcomes.filter((o) => fired.has(o.name)),
          ...outcomes.filter((o) => !fired.has(o.name)),
        ]
      : outcomes;
  const match = ordered.find((o) => o.severity === severity && o.label);
  if (match) return displayName(match.label);
  return (
    displayName(item.outcomeOptions?.scoreLabels?.[severity]) ||
    PLAIN_SEVERITY_NAMES[severity]
  );
};

/**
 * Every surface reads the client's configured name for a result; the severity
 * word is only ever a fallback for a result the client never named. SentiLink
 * carries its own check state, which is the name of that result.
 */
export const lumenLabel = (item: NamingSource): string => {
  if (item.rowKind === Vero1RowKind.Sentilink) {
    return (
      checkStateLabel(item.originalValue) ??
      clientNameFor(item.originalSeverity, item) ??
      severityLabel(item.originalSeverity)
    );
  }
  return clientNameFor(item.originalSeverity, item) ?? NO_VERDICT_LABEL;
};

/** The wire list wins; the derivation covers payloads that predate it. */
export const availableSeverities = (item: NamingSource): Vero1Severity[] => {
  if (item.availableSeverities) return [...item.availableSeverities];
  if (item.rowKind === Vero1RowKind.Custom) return [...SEVERITY_ORDER];
  if (item.rowKind !== Vero1RowKind.Outcome) return [];
  const configured = new Set(outcomesOf(item).map((o) => o.severity));
  return SEVERITY_ORDER.filter((s) => s === "pass" || configured.has(s));
};

export const hasWarnOption = (item: NamingSource): boolean =>
  availableSeverities(item).includes("warn");

/** Mirrors Vero1ChecklistItem.name_candidates: the wire list, else the
 *  configured labels at the severity plus the run's pass score label. */
export const nameCandidatesFor = (
  severity: Vero1Severity | null | undefined,
  item: NamingSource
): string[] => {
  if (!severity || item.rowKind === Vero1RowKind.Sentilink) return [];
  const wire = item.nameCandidates?.[severity];
  if (wire) return [...wire];
  const candidates: string[] = [];
  const push = (label?: string | null) => {
    const name = label ? stripPriorityPrefix(label) || label : "";
    if (name.trim() && !candidates.includes(name)) candidates.push(name);
  };
  outcomesOf(item)
    .filter((o) => o.severity === severity)
    .forEach((o) => push(o.label));
  if (severity === "pass") push(item.outcomeOptions?.scoreLabels?.pass);
  return candidates;
};

const RECORD_LABEL_FIELDS = [
  "plaintiff",
  "creditor",
  "originalCreditor",
  "agency",
  "collectionAgency",
  "offense",
  "charge",
  "description",
  "court",
  "caseType",
  "type",
  "name",
];

/** Names arrive from the bureaus as LAST*FIRST(*MIDDLE), the asterisk a raw
 *  delimiter, not part of the name. Reads "Richard Levine", not "LEVINE*RICHARD".
 *  Left untouched when the field isn't a starred name. */
const cleanPartyName = (raw: string): string => {
  if (!raw.includes("*")) return raw;
  const parts = raw
    .split("*")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return raw.replace(/\*/g, " ").trim();
  const [last, first, ...rest] = parts;
  const titleCase = (w: string) =>
    /[a-z]/.test(w) ? w : w.charAt(0) + w.slice(1).toLowerCase();
  return [first, ...rest, last].map(titleCase).join(" ");
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Some criteria (criminal-records) arrive as a JSON *string* rather than a
 * parsed array, depending on how the formula annotated the run. Normalising
 * here keeps every caller from having to know which is which.
 */
export const parseValue = (value: unknown): unknown => {
  let current = value;

  // criminal-records arrives double-encoded — a JSON string whose contents are
  // themselves a JSON array — so unwrap until it stops being a string. Bounded
  // so a pathological value can't spin.
  for (let depth = 0; depth < 3; depth += 1) {
    if (typeof current !== "string") {
      return current;
    }
    const trimmed = current.trim();
    // live runs deliver money/score/ratio values as BARE NUMERIC STRINGS
    // ("645", "0.208", "500000"), which display as numbers, not as text
    if (trimmed !== "" && Number.isFinite(Number(trimmed))) {
      return Number(trimmed);
    }
    // a double-encoded value is a quoted string, so `"` has to count as
    // parseable too — testing only for [ or { is what left it unwrapped
    if (!/^[[{"]/.test(trimmed)) {
      return current;
    }
    let next: unknown;
    try {
      next = JSON.parse(trimmed);
    } catch {
      return current;
    }
    // a quoted plain string parses to itself minus the quotes; stop rather
    // than loop on it
    if (typeof next === "string" && next === trimmed) {
      return next;
    }
    current = next;
  }

  return current;
};

const money = (amount: number) =>
  `$${amount.toLocaleString(
    undefined,
    amount % 1
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: 0 }
  )}`;

/** A single record as one line: the most identifying field, date and amount. */
export const formatRecord = (record: Record<string, unknown>): string => {
  const parts: string[] = [];

  const labelField = RECORD_LABEL_FIELDS.find(
    (field) => typeof record[field] === "string" && record[field]
  );
  if (labelField) {
    const raw = String(record[labelField]);
    // party-name fields carry the LAST*FIRST delimiter; detail fields don't
    const isName = labelField === "plaintiff" || labelField === "name";
    parts.push(isName ? cleanPartyName(raw) : raw);
  }

  if (typeof record.date === "string" && record.date) {
    parts.push(formatDate(record.date));
  }

  if (typeof record.amount === "number") {
    parts.push(money(record.amount));
  } else if (typeof record.balance === "number") {
    parts.push(money(record.balance));
  }

  // a default judgement is the detail that changes how a record reads, so it
  // survives the summary even though the rest of the object does not
  if (record.defaultJudgement === true) {
    parts.push("default judgement");
  }

  return parts.length ? parts.join(" · ") : "Record details unavailable";
};

/** Count line for a records criterion — "2 records" / "No records". */
export const formatRecordCount = (records: unknown[]): string => {
  if (!records.length) {
    return "None on file";
  }
  return `${records.length} ${records.length === 1 ? "record" : "records"}`;
};

/**
 * Display string for a criterion value. Returns the count line for record
 * arrays; callers wanting the individual lines use `getRecords`.
 */
export const formatItemValue = (raw: unknown, criterionKey = ""): string => {
  const value = parseValue(raw);

  if (isScoreCriterion(criterionKey) && isMissingScore(value)) {
    return NO_SCORE_LABEL;
  }

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  // lumen's history caps stringified results at 256 chars — a cut records
  // array is unparseable JSON (possibly still wrapped in its outer quote when
  // double-encoded), and the count past the cut is unverifiable
  if (typeof value === "string" && /^[[{"]/.test(value.trim())) {
    const body = value.trim().replace(/^"\s*/, "");
    if (body.startsWith("[")) {
      const started = (body.match(/\{/g) ?? []).length;
      return started === 1 ? "Record on file" : "Records on file";
    }
    if (body.startsWith("{")) {
      return "Record details unavailable";
    }
    return value;
  }

  if (typeof value === "number") {
    if (MONEY_CRITERIA.has(criterionKey)) {
      return money(value / 100);
    }
    if (RATIO_CRITERIA.has(criterionKey)) {
      return `${value}x`;
    }
  }

  if (Array.isArray(value)) {
    return formatRecordCount(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return value.toLocaleString();
  }

  if (isRecord(value)) {
    return formatRecord(value);
  }

  return String(value);
};

/** The individual record lines behind a records criterion, if it is one. */
export const getRecords = (raw: unknown): string[] => {
  const value = parseValue(raw);
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) =>
    isRecord(entry) ? formatRecord(entry) : String(entry)
  );
};
